import { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Plus, Volume2, Play, Image as ImageIcon, X, Check, Sparkles, AlertCircle } from 'lucide-react';
import { haptic } from '../lib/telegram';

interface MediaFile {
  id: string;
  file: File;
  type: 'audio' | 'video' | 'image' | 'zip' | 'project';
  preview: string;
}

import { ProductType, DELIVERY_OPTIONS_BY_TYPE, DAW_OPTIONS } from './types';
type UploadStep = 1 | 2 | 3 | 4;

const MAX_PREVIEW_BYTES = 20 * 1024 * 1024; // 20 MB
const MAX_COVER_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_DELIVERABLE_BYTES = 200 * 1024 * 1024; // 200 MB per file

interface UploadWorkViewProps {
  onBack: () => void;
  onPublish: (data: {
    title: string;
    description: string;
    tags: string[];
    price: string;
    category: string;
    bpm: string;
    key: string;
    daw?: string;
    productType: ProductType;
    deliveryFormats: string[];
    previewFile: MediaFile | null;
    coverFile: MediaFile | null;
    deliverableFiles: MediaFile[];
    isReadyMade?: boolean;
  }) => void;
}

export function UploadWorkView({ onBack, onPublish }: UploadWorkViewProps) {
  const [step, setStep] = useState<UploadStep>(1);
  const [productType, setProductType] = useState<ProductType>('beat');
  const [deliveryOptions, setDeliveryOptions] = useState<string[]>([]);
  const [previewFile, setPreviewFile] = useState<MediaFile | null>(null);
  const [coverFile, setCoverFile] = useState<MediaFile | null>(null);
  const [deliverableFiles, setDeliverableFiles] = useState<MediaFile[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [price, setPrice] = useState('');
  const [bpm, setBpm] = useState('');
  const [key, setKey] = useState('');
  const [daw, setDaw] = useState<string>('');
  const [isReadyMade, setIsReadyMade] = useState(true);
  const [validationError, setValidationError] = useState<string>('');

  const mainInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const deliverableInputRef = useRef<HTMLInputElement>(null);

  const hasVideoPreview = previewFile?.type === 'video';
  const needsCoverStep = !hasVideoPreview && productType !== 'cover';
  const showBpmKey = ['beat', 'feature'].includes(productType);
  const showDaw = deliveryOptions.includes('project');

  const cleanupMedia = () => {
    if (previewFile?.preview) URL.revokeObjectURL(previewFile.preview);
    if (coverFile?.preview) URL.revokeObjectURL(coverFile.preview);
    deliverableFiles.forEach((m) => {
      if (m.preview) URL.revokeObjectURL(m.preview);
    });
  };

  useEffect(() => () => { cleanupMedia(); }, []);

  // Validate file uploads based on selected delivery options
  const validateDeliverableFiles = (): string | null => {
    if (!deliveryOptions.length) return null;

    // Covers are services - require modification note
    if (productType === 'cover') {
      return null; // Covers don't need file validation as they're services
    }

    // MP3 only validation
    if (deliveryOptions.includes('mp3') && deliveryOptions.length === 1) {
      const mp3Files = deliverableFiles.filter(f => f.file.name.toLowerCase().endsWith('.mp3'));
      if (mp3Files.length !== 1) {
        return 'Для MP3 потрібен рівно 1 MP3 файл';
      }
    }

    // WAV only validation
    if (deliveryOptions.includes('wav') && deliveryOptions.length === 1 && !deliveryOptions.includes('mp3+wav')) {
      const wavFiles = deliverableFiles.filter(f => f.file.name.toLowerCase().endsWith('.wav'));
      if (wavFiles.length !== 1) {
        return 'Для WAV потрібен рівно 1 WAV файл';
      }
    }

    // MP3 + WAV validation (strict)
    if (deliveryOptions.includes('mp3+wav')) {
      const mp3Files = deliverableFiles.filter(f => f.file.name.toLowerCase().endsWith('.mp3'));
      const wavFiles = deliverableFiles.filter(f => f.file.name.toLowerCase().endsWith('.wav'));
      if (mp3Files.length !== 1 || wavFiles.length !== 1) {
        return 'Для MP3 + WAV потрібно 1 MP3 файл і 1 WAV файл';
      }
    }

    // Stems validation (requires ZIP)
    if (deliveryOptions.includes('stems')) {
      const zipFiles = deliverableFiles.filter(f => f.file.name.toLowerCase().endsWith('.zip'));
      if (zipFiles.length === 0) {
        return 'Для Stems потрібен ZIP файл';
      }
    }

    // Project file validation (requires ZIP or project files)
    if (deliveryOptions.includes('project')) {
      const projectFiles = deliverableFiles.filter(f =>
        f.file.name.toLowerCase().endsWith('.zip') ||
        f.file.name.toLowerCase().endsWith('.flp') ||
        f.file.name.toLowerCase().endsWith('.als') ||
        f.file.name.toLowerCase().endsWith('.logicx')
      );
      if (projectFiles.length === 0) {
        return 'Для Project File потрібен ZIP або файл проєкту DAW';
      }
      if (!daw) {
        return 'Оберіть DAW для проєктного файлу';
      }
    }

    // Kit validation (requires ZIP)
    if (productType === 'kit') {
      const zipFiles = deliverableFiles.filter(f => f.file.name.toLowerCase().endsWith('.zip'));
      if (zipFiles.length === 0) {
        return 'Для Kit потрібен ZIP файл';
      }
    }

    return null;
  };

  const handleFinalPublish = () => {
    const error = validateDeliverableFiles();
    if (error) {
      setValidationError(error);
      haptic.error();
      return;
    }

    const tagArray = tags.split(',').map((t) => t.trim()).filter(Boolean);
    onPublish({
      title,
      description,
      tags: tagArray,
      price,
      category: productType,
      bpm: showBpmKey ? bpm : '',
      key: showBpmKey ? key : '',
      daw: showDaw ? daw : undefined,
      productType,
      deliveryFormats: deliveryOptions,
      previewFile,
      coverFile,
      deliverableFiles,
      isReadyMade: productType === 'cover' ? isReadyMade : undefined,
    });
  };

  const canGoNext =
    (step === 1 && productType && title && description && tags && price) ||
    (step === 2 && !!previewFile) ||
    (step === 3 && (!needsCoverStep || !!coverFile)) ||
    (step === 4 && (DELIVERY_OPTIONS_BY_TYPE[productType].length === 0 || deliverableFiles.length > 0 || (productType === 'cover' && !isReadyMade)));

  const handleNextOrPublish = () => {
    if (!canGoNext) return;

    if (step === 2) {
      if (hasVideoPreview || !needsCoverStep) {
        setStep(4);
        return;
      }
      setStep(3);
      return;
    }

    if (step === 3) {
      setStep(4);
      return;
    }

    if (step < 4) {
      setStep((s) => (s + 1) as UploadStep);
    } else {
      handleFinalPublish();
    }
  };

  const handleFileUpload = (files: FileList | null, target: 'preview' | 'cover' | 'deliverable') => {
    if (!files || files.length === 0) return;

    const processFile = (file: File): MediaFile | string => {
      const isVideo = file.type.startsWith('video/');
      const isAudio = file.type.startsWith('audio/');
      const isImage = file.type.startsWith('image/');
      const isZip = file.type === 'application/zip' || file.name.toLowerCase().endsWith('.zip');
      const isProject = ['.flp', '.als', '.logicx'].some(ext => file.name.toLowerCase().endsWith(ext));

      if (target === 'preview' && !isAudio && !isVideo) {
        return 'Дозволені лише аудіо- та відеофайли для попереднього перегляду.';
      }
      if (target === 'cover' && !isImage) {
        return 'Для обкладинки дозволені лише файли зображень.';
      }

      const maxSize = target === 'preview' ? MAX_PREVIEW_BYTES :
                      target === 'cover' ? MAX_COVER_BYTES :
                      MAX_DELIVERABLE_BYTES;

      if (file.size > maxSize) {
        return `Файл завеликий (макс ${Math.round(maxSize / 1024 / 1024)} MB)`;
      }

      const preview = (isVideo || isAudio || isImage) ? URL.createObjectURL(file) : '';
      let type: MediaFile['type'] = 'audio';
      if (isVideo) type = 'video';
      else if (isImage) type = 'image';
      else if (isZip) type = 'zip';
      else if (isProject) type = 'project';

      return {
        id: `${target}-${Date.now()}`,
        file,
        type,
        preview,
      };
    };

    if (target === 'deliverable') {
      const newFiles: MediaFile[] = [];
      for (const file of Array.from(files)) {
        const result = processFile(file);
        if (typeof result === 'string') {
          setValidationError(result);
          haptic.error();
          return;
        }
        newFiles.push(result);
      }
      deliverableFiles.forEach(m => { if (m.preview) URL.revokeObjectURL(m.preview); });
      setDeliverableFiles(newFiles);
    } else {
      const file = files[0];
      const result = processFile(file);
      if (typeof result === 'string') {
        setValidationError(result);
        haptic.error();
        return;
      }
      if (target === 'preview') {
        if (previewFile?.preview) URL.revokeObjectURL(previewFile.preview);
        setPreviewFile(result);
      } else {
        if (coverFile?.preview) URL.revokeObjectURL(coverFile.preview);
        setCoverFile(result);
      }
    }

    setValidationError('');
  };

  return (
    <div className="min-h-screen bg-splyt-gradient flex flex-col">
      {/* Header - Privacy style */}
      <div className="relative z-10 px-4 pt-4 pb-2 flex items-center gap-4 border-b border-white/10 bg-black/20 backdrop-blur-xl sticky top-0">
        <button
          onClick={() => {
            haptic.light();
            onBack();
          }}
          className="w-10 h-10 rounded-full border backdrop-blur-xl flex items-center justify-center transition-all hover:border-white/30"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'rgba(255, 255, 255, 0.12)',
          }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
        </button>
        <h1 className="text-xl font-bold text-white">Завантажити роботу</h1>
      </div>

      {/* Content */}
      <div className="relative z-10 p-4 space-y-6 flex-1 overflow-y-auto pb-24">
        {/* Step Indicator */}
        <div className="flex justify-center gap-2">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-all ${
                step >= s ? 'bg-splyt-green w-8' : 'bg-white/20'
              }`}
            />
          ))}
        </div>

        {/* Validation Error */}
        {validationError && (
          <div className="p-3 rounded-xl border flex items-start gap-2" style={{
            background: 'rgba(239, 68, 68, 0.1)',
            borderColor: 'rgba(239, 68, 68, 0.3)',
          }}>
            <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-400" />
            <p className="text-red-400 text-sm">{validationError}</p>
          </div>
        )}

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-4">
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', marginBottom: '8px' }}>
              ОСНОВНА ІНФОРМАЦІЯ
            </p>

            <div className="p-4 rounded-xl border space-y-4" style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
            }}>
              {/* Category */}
              <div>
                <label className="block text-sm text-white/70 mb-2">Категорія</label>
                <select
                  value={productType}
                  onChange={(e) => {
                    const nextType = e.target.value as ProductType;
                    setProductType(nextType);
                    setDeliveryOptions([]);
                    setBpm('');
                    setKey('');
                    setDaw('');
                    haptic.selection();
                  }}
                  className="w-full px-4 py-3 rounded-xl border outline-none transition-all focus:border-splyt-green/50 text-white bg-transparent"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                  }}
                >
                  <option value="beat">Біт</option>
                  <option value="cover">Дизайн (Cover Art)</option>
                  <option value="feature">Фіт (Feature)</option>
                  <option value="mix">Зведення (Mixing)</option>
                  <option value="kit">Кіт (Kit)</option>
                  <option value="service">Сервіс (Service)</option>
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm text-white/70 mb-2">Назва роботи</label>
                <input
                  type="text"
                  placeholder="Введіть назву..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border outline-none transition-all focus:border-splyt-green/50 text-white placeholder:text-white/30"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                  }}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm text-white/70 mb-2">Опис</label>
                <textarea
                  placeholder="Детальний опис роботи..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border outline-none transition-all focus:border-splyt-green/50 resize-none text-white placeholder:text-white/30"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                  }}
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm text-white/70 mb-2">Теги (через кому)</label>
                <input
                  type="text"
                  placeholder="trap, hip-hop, dark..."
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border outline-none transition-all focus:border-splyt-green/50 text-white placeholder:text-white/30"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                  }}
                />
              </div>

              {/* BPM & Key - Only for beats and features */}
              {showBpmKey && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">BPM</label>
                    <input
                      type="text"
                      placeholder="140"
                      value={bpm}
                      onChange={(e) => setBpm(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border outline-none transition-all focus:border-splyt-green/50 text-white placeholder:text-white/30"
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderColor: 'rgba(255, 255, 255, 0.12)',
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Key</label>
                    <input
                      type="text"
                      placeholder="C#m"
                      value={key}
                      onChange={(e) => setKey(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border outline-none transition-all focus:border-splyt-green/50 text-white placeholder:text-white/30"
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderColor: 'rgba(255, 255, 255, 0.12)',
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Dimensions - Only for cover art/design */}
              {productType === 'cover' && (
                <>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Розмір (напр. 3000x3000px)</label>
                    <input
                      type="text"
                      placeholder="3000x3000"
                      value={bpm}
                      onChange={(e) => setBpm(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border outline-none transition-all focus:border-splyt-green/50 text-white placeholder:text-white/30"
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderColor: 'rgba(255, 255, 255, 0.12)',
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Тип</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setIsReadyMade(true)}
                        className={`flex-1 py-3 rounded-xl border text-sm ${
                          isReadyMade ? 'border-splyt-green text-white' : 'border-white/20 text-white/70'
                        }`}
                        style={{
                          background: isReadyMade ? 'rgba(0, 255, 136, 0.15)' : 'rgba(255, 255, 255, 0.03)',
                        }}
                      >
                        Готовий продукт
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsReadyMade(false)}
                        className={`flex-1 py-3 rounded-xl border text-sm ${
                          !isReadyMade ? 'border-splyt-green text-white' : 'border-white/20 text-white/70'
                        }`}
                        style={{
                          background: !isReadyMade ? 'rgba(0, 255, 136, 0.15)' : 'rgba(255, 255, 255, 0.03)',
                        }}
                      >
                        Послуга (на замовлення)
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Price */}
              <div>
                <label className="block text-sm text-white/70 mb-2">Ціна (UAH)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg text-white/50">₴</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 rounded-xl border outline-none transition-all focus:border-splyt-green/50 text-white placeholder:text-white/30"
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderColor: 'rgba(255, 255, 255, 0.12)',
                    }}
                  />
                </div>
              </div>

              {/* Delivery Options */}
              {DELIVERY_OPTIONS_BY_TYPE[productType].length > 0 && (
                <div>
                  <label className="block text-sm text-white/70 mb-2">
                    Що отримає клієнт?
                    {productType === 'cover' && (
                      <span className="block text-xs text-white/50 mt-1">
                        Дизайн вимагає модифікації/підготовки перед доставкою
                      </span>
                    )}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {DELIVERY_OPTIONS_BY_TYPE[productType].map((opt) => {
                      const active = deliveryOptions.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => {
                            haptic.light();
                            setDeliveryOptions((prev) =>
                              active ? prev.filter((id) => id !== opt.id) : [...prev, opt.id]
                            );
                          }}
                          className={`px-3 py-2 rounded-xl text-xs border transition-all ${
                            active
                              ? 'border-splyt-green text-white'
                              : 'border-white/20 text-white/70'
                          }`}
                          style={{
                            background: active ? 'rgba(0, 255, 136, 0.15)' : 'rgba(255, 255, 255, 0.03)',
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* DAW Selection - Only when project file selected */}
              {showDaw && (
                <div>
                  <label className="block text-sm text-white/70 mb-2">DAW (Digital Audio Workstation)</label>
                  <select
                    value={daw}
                    onChange={(e) => {
                      setDaw(e.target.value);
                      haptic.selection();
                    }}
                    className="w-full px-4 py-3 rounded-xl border outline-none transition-all focus:border-splyt-green/50 text-white bg-transparent"
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderColor: 'rgba(255, 255, 255, 0.12)',
                    }}
                  >
                    <option value="">Оберіть DAW...</option>
                    {DAW_OPTIONS.map(dawOption => (
                      <option key={dawOption} value={dawOption}>{dawOption}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 2 && (
          <div className="space-y-4">
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', marginBottom: '8px' }}>
              ПРЕВ'Ю (АУДІО/ВІДЕО)
            </p>

            <div className="p-4 rounded-xl border space-y-4" style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
            }}>
              <input
                ref={mainInputRef}
                type="file"
                accept="audio/*,video/*"
                onChange={(e) => handleFileUpload(e.target.files, 'preview')}
                className="hidden"
              />

              <button
                type="button"
                onClick={() => {
                  haptic.light();
                  mainInputRef.current?.click();
                }}
                className="w-full py-3 rounded-xl border text-white flex items-center justify-center gap-2 transition-all hover:border-splyt-green/50"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                }}
              >
                <Plus className="w-5 h-5" />
                {previewFile ? 'Змінити превʼю' : 'Додати превʼю'}
              </button>

              {previewFile && (
                <div className="flex items-center gap-3 p-3 rounded-xl" style={{
                  background: 'rgba(0, 255, 136, 0.05)',
                  border: '1px solid rgba(0, 255, 136, 0.2)',
                }}>
                  {previewFile.type === 'audio' ? (
                    <Volume2 className="w-4 h-4 text-splyt-green flex-shrink-0" />
                  ) : (
                    <Play className="w-4 h-4 text-splyt-green flex-shrink-0" />
                  )}
                  <span className="truncate text-white text-sm">{previewFile.file.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      haptic.light();
                      if (previewFile.preview) URL.revokeObjectURL(previewFile.preview);
                      setPreviewFile(null);
                    }}
                    className="ml-auto w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0, 0, 0, 0.4)' }}
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Cover (skipped for video preview and design category) */}
        {step === 3 && needsCoverStep && (
          <div className="space-y-4">
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', marginBottom: '8px' }}>
              ОБКЛАДИНКА
            </p>

            <div className="p-4 rounded-xl border space-y-4" style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
            }}>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e.target.files, 'cover')}
                className="hidden"
              />

              <button
                type="button"
                onClick={() => {
                  haptic.light();
                  coverInputRef.current?.click();
                }}
                className="w-full py-3 rounded-xl border text-white flex items-center justify-center gap-2 transition-all hover:border-splyt-green/50"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                }}
              >
                <ImageIcon className="w-5 h-5" />
                {coverFile ? 'Змінити обкладинку' : 'Додати обкладинку'}
              </button>

              {coverFile && (
                <div className="relative">
                  <img
                    src={coverFile.preview}
                    alt="Cover"
                    className="w-full rounded-xl object-cover border"
                    style={{ borderColor: 'rgba(0, 255, 136, 0.3)' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      haptic.light();
                      if (coverFile.preview) URL.revokeObjectURL(coverFile.preview);
                      setCoverFile(null);
                    }}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0, 0, 0, 0.7)' }}
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Deliverable Files */}
        {step === 4 && (
          <div className="space-y-4">
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', marginBottom: '8px' }}>
              ФАЙЛИ ДЛЯ ДОСТАВКИ
            </p>

            <div className="p-4 rounded-xl border space-y-4" style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
            }}>
              {DELIVERY_OPTIONS_BY_TYPE[productType].length === 0 ? (
                <p className="text-sm text-white/60">
                  Для цього типу товару клієнт не отримує окремий файл — це сервіс/робота.
                </p>
              ) : (
                <>
                  <p className="text-sm text-white/70">
                    Завантажте файли у обраних форматах ({deliveryOptions.join(', ') || 'не обрано'})
                  </p>

                  <input
                    ref={deliverableInputRef}
                    type="file"
                    multiple
                    accept={deliveryOptions.includes('mp3') || deliveryOptions.includes('mp3+wav') || deliveryOptions.includes('wav') ? 'audio/*,.zip,.flp,.als,.logicx' : deliveryOptions.includes('zip') || deliveryOptions.includes('stems') ? '.zip,application/zip' : deliveryOptions.includes('project') ? '.zip,.flp,.als,.logicx' : '*/*'}
                    onChange={(e) => handleFileUpload(e.target.files, 'deliverable')}
                    className="hidden"
                  />

                  <button
                    type="button"
                    onClick={() => {
                      haptic.light();
                      deliverableInputRef.current?.click();
                    }}
                    className="w-full py-3 rounded-xl border text-white flex items-center justify-center gap-2 transition-all hover:border-splyt-green/50"
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderColor: 'rgba(255, 255, 255, 0.12)',
                    }}
                  >
                    <Plus className="w-5 h-5" />
                    Додати файли
                  </button>

                  {deliverableFiles.length > 0 && (
                    <div className="space-y-2">
                      {deliverableFiles.map((f) => (
                        <div
                          key={f.id}
                          className="flex items-center gap-2 p-2 rounded-lg"
                          style={{
                            background: 'rgba(0, 255, 136, 0.05)',
                            border: '1px solid rgba(0, 255, 136, 0.2)',
                          }}
                        >
                          <span className="truncate text-white text-sm flex-1">{f.file.name}</span>
                          <span className="text-xs text-white/50">
                            {(f.file.size / 1024 / 1024).toFixed(1)} MB
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              haptic.light();
                              if (f.preview) URL.revokeObjectURL(f.preview);
                              setDeliverableFiles((prev) => prev.filter((x) => x.id !== f.id));
                            }}
                            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: 'rgba(0, 0, 0, 0.4)' }}
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* File Requirements Info */}
                  {deliveryOptions.includes('mp3+wav') && (
                    <div className="p-3 rounded-xl border" style={{
                      background: 'rgba(0, 255, 136, 0.05)',
                      borderColor: 'rgba(0, 255, 136, 0.2)',
                    }}>
                      <p className="text-xs text-white/70">
                        <Check className="w-3 h-3 inline mr-1 text-splyt-green" />
                        Потрібно: 1 MP3 файл + 1 WAV файл
                      </p>
                    </div>
                  )}

                  {(deliveryOptions.includes('stems') || deliveryOptions.includes('project') || productType === 'kit') && (
                    <div className="p-3 rounded-xl border" style={{
                      background: 'rgba(0, 255, 136, 0.05)',
                      borderColor: 'rgba(0, 255, 136, 0.2)',
                    }}>
                      <p className="text-xs text-white/70">
                        <Check className="w-3 h-3 inline mr-1 text-splyt-green" />
                        Потрібно: ZIP файл або файл проєкту DAW
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Guidelines */}
            <div className="p-4 rounded-xl border space-y-2" style={{
              background: 'rgba(255, 255, 255, 0.02)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
            }}>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-splyt-green" />
                <p className="text-sm text-white/60">
                  Тільки якісне аудіо/відео/зображення
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-splyt-green" />
                <p className="text-sm text-white/60">
                  Без матеріалів з авторськими правами
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5 text-splyt-green" />
                <p className="text-sm text-white/60">
                  Чіткий і точний опис
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 z-20 p-4 border-t border-white/10"
           style={{ background: 'linear-gradient(to top, rgba(11, 18, 32, 1) 70%, rgba(11, 18, 32, 0))' }}>
        <button
          onClick={() => {
            haptic.medium();
            handleNextOrPublish();
          }}
          disabled={!canGoNext}
          className="w-full py-4 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold"
          style={{
            background: canGoNext ? 'linear-gradient(135deg, #00FF88 0%, #00CC6A 100%)' : 'rgba(255, 255, 255, 0.1)',
            boxShadow: canGoNext ? '0 4px 20px rgba(0, 255, 136, 0.3)' : 'none',
          }}
        >
          <span>{step < 4 ? 'Продовжити' : 'Опублікувати'}</span>
          <Sparkles className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
